import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchAllAktienUebersicht,
  setSuchbegriff,
  selectAktie,
  selectAktienListe,
  selectAktienListeStatus,
  selectSuchbegriff,
} from 'modules/aktien/features/aktienDbSlice';
import { formatKurs, formatProz, makeComparator } from 'core/utils/format';
import SortHeader from 'core/components/SortHeader';
import UrteilZelle from 'modules/aktien/components/UrteilZelle';

/** Sortierwert einer Zeile je Spaltenschlüssel. */
const getValue = (a, key) => {
  if (key === 'aktienName') return a.aktienName;
  if (key === 'aktuellWert') return a.aktuellPreis ?? a.schlusskurs;
  if (key === 'aenderungProz') return a.aenderungProz;
  if (key === 'urteil') return a.gesamtUrteilScore;
  return null;
};

/**
 * Reiter „Liste DB": alle in der Datenbank gespeicherten Aktien mit Name und
 * aktuellem Wert. Alphabetisch nach Name sortiert, per Suchfeld auf den Namen
 * gefiltert. Klick auf eine Zeile öffnet die Detailansicht.
 *
 * Der Suchbegriff liegt im Redux-State, damit er beim Zurückkehren aus der
 * Detailansicht erhalten bleibt.
 */
export default function ListeDbView() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const liste = useSelector(selectAktienListe);
  const status = useSelector(selectAktienListeStatus);
  const suchbegriff = useSelector(selectSuchbegriff);

  // Sortierung: standardmäßig alphabetisch nach Name (aufsteigend).
  const [sortKey, setSortKey] = useState('aktienName');
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      // Zahlenspalten zuerst absteigend (größte Werte oben), Text aufsteigend.
      setSortDir(key === 'aktienName' ? 'asc' : 'desc');
    }
  };

  // Liste einmalig laden.
  useEffect(() => {
    if (status === 'idle') {
      dispatch(fetchAllAktienUebersicht());
    }
  }, [dispatch, status]);

  // Nach Name filtern und nach gewählter Spalte sortieren.
  const gefiltert = useMemo(() => {
    const term = suchbegriff.trim().toLowerCase();
    return [...liste]
      .filter((a) => !term || (a.aktienName || '').toLowerCase().includes(term))
      .sort(makeComparator((row) => getValue(row, sortKey), sortDir));
  }, [liste, suchbegriff, sortKey, sortDir]);

  const handleOpen = (isin) => {
    dispatch(selectAktie(isin));
    navigate(`/projekte/aktienanalyse/aktie/${isin}`);
  };

  // Navigation per Button (Detail/Analyse) ohne den Zeilen-Klick auszulösen.
  const handleNav = (e, isin, view) => {
    e.stopPropagation();
    dispatch(selectAktie(isin));
    navigate(`/projekte/aktienanalyse/${view}/${isin}`);
  };

  return (
    <div>
      <header className="mb-6">
        <h1 className="font-serif text-3xl font-semibold text-ink">Liste DB</h1>
        <p className="mt-1 text-sm text-ink-muted">
          Alle gespeicherten Aktien aus der Datenbank mit ihrem zuletzt
          erfassten Wert.
        </p>
      </header>

      {/* Suchfeld */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-ink-muted">
            🔍
          </span>
          <input
            type="search"
            value={suchbegriff}
            onChange={(e) => dispatch(setSuchbegriff(e.target.value))}
            placeholder="Nach Name suchen…"
            className="w-full rounded-lg border border-line bg-paper-soft py-2 pl-10 pr-3 text-sm text-ink placeholder-ink-muted outline-none transition-colors focus:border-ink/30 focus:ring-1 focus:ring-ink/20"
          />
        </div>
      </div>

      {status === 'loading' && <p className="text-ink-muted">Lade Aktien…</p>}
      {status === 'failed' && (
        <p className="rounded-xl border border-dashed border-loss/30 bg-loss/5 p-8 text-center text-loss">
          Die Aktienliste konnte nicht geladen werden.
        </p>
      )}

      {status === 'succeeded' &&
        (gefiltert.length === 0 ? (
          <p className="rounded-xl border border-dashed border-line bg-paper-soft p-8 text-center text-ink-muted">
            {liste.length === 0
              ? 'Noch keine Aktien in der Datenbank.'
              : 'Keine Aktie passt zur Suche.'}
          </p>
        ) : (
          <div className="overflow-hidden rounded-xl border border-line bg-paper-soft">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-line bg-paper-sunken text-left font-mono text-xs uppercase tracking-wider text-ink-muted">
                  <SortHeader
                    label="Name"
                    columnKey="aktienName"
                    sortKey={sortKey}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                  <SortHeader
                    label="Aktueller Wert"
                    columnKey="aktuellWert"
                    align="right"
                    sortKey={sortKey}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                  <SortHeader
                    label="Änderung"
                    columnKey="aenderungProz"
                    align="right"
                    sortKey={sortKey}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                  <SortHeader
                    label="Urteil"
                    columnKey="urteil"
                    align="right"
                    sortKey={sortKey}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                </tr>
              </thead>
              <tbody>
                {gefiltert.map((a) => {
                  const positiv = (a.aenderungProz ?? 0) >= 0;
                  return (
                    <tr
                      key={a.isin}
                      onClick={() => handleOpen(a.isin)}
                      className="cursor-pointer border-t border-line transition-colors hover:bg-ink/[0.03]"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="min-w-0">
                            <div className="truncate font-medium text-ink">
                              {a.aktienName}
                            </div>
                            <div className="font-mono text-xs text-ink-muted">
                              {a.isin}
                            </div>
                          </div>
                          <div className="flex shrink-0 gap-1">
                            <button
                              type="button"
                              onClick={(e) => handleNav(e, a.isin, 'aktie')}
                              className="rounded-md border border-line bg-paper px-2 py-1 text-xs font-medium text-ink-muted transition-colors hover:bg-ink/5 hover:text-ink"
                              title="Zur Detailansicht"
                            >
                              📊 Detail
                            </button>
                            <button
                              type="button"
                              onClick={(e) => handleNav(e, a.isin, 'analyse')}
                              className="rounded-md border border-line bg-paper px-2 py-1 text-xs font-medium text-ink-muted transition-colors hover:bg-ink/5 hover:text-ink"
                              title="Zur Analyse"
                            >
                              🔬 Analyse
                            </button>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums text-ink">
                        {formatKurs(a.aktuellPreis ?? a.schlusskurs, a.currency)}
                      </td>
                      <td
                        className={`px-4 py-3 text-right font-mono font-medium tabular-nums ${
                          a.aenderungProz == null
                            ? 'text-ink-muted'
                            : positiv
                            ? 'text-gain'
                            : 'text-loss'
                        }`}
                      >
                        {formatProz(a.aenderungProz)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <UrteilZelle
                          ampel={a.gesamtUrteilAmpel}
                          score={a.gesamtUrteilScore}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}

      {status === 'succeeded' && gefiltert.length > 0 && (
        <p className="mt-3 font-mono text-xs text-ink-muted">
          {gefiltert.length} von {liste.length} Aktien
        </p>
      )}
    </div>
  );
}
